import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Flex, Text, Badge, Spinner, Button, Select, Switch, SimpleGrid, Input, IconButton, useToast, useColorModeValue,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure,
} from '@chakra-ui/react';
import { MdDelete } from 'react-icons/md';
import Card from 'components/card/Card';
import Pagination, { useServerPaged, useDebounced } from 'components/vdh/Pagination';
import { api } from 'lib/api';
import { useApi } from 'lib/useApi';

const AVATARS = ['🦊', '🐨', '🐰', '🦁', '🐝', '🐿️', '🐼', '🐧', '🦉', '🐢', '🦄', '🐬'];
const kidAva = (c) => c?.avatar || AVATARS[[...(c?.name || '')].reduce((a, ch) => a + ch.charCodeAt(0), 0) % AVATARS.length];
const TIER_PILL = { pro: ['PRO', 'green'], standard: ['STANDARD', 'blue'], free: ['FREE', 'gray'] };
/** Thời điểm ghi nhận giao dịch — cần cả giờ vì gói test của Play gia hạn vài phút một lần. */
export const fmtDateTime = (v) => (v
  ? new Date(v).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—');
/** Nhãn gói của 1 kỳ — kỳ dùng thử hiện TRIAL, không hiện PRO (không phải gói đã mua). */
export const RecordTier = ({ tier, source }) => (source === 'trial'
  ? <Badge colorScheme="purple">TRIAL</Badge>
  : <Badge colorScheme={tier === 'pro' ? 'green' : 'blue'}>{(tier || '').toUpperCase()}</Badge>);
/**
 * Vị trí của 1 kỳ so với hiện tại. Mua khi kỳ cũ chưa hết hạn sẽ tạo kỳ nối tiếp
 * (bắt đầu = ngày kết thúc kỳ trước), nên có thể xếp hàng nhiều kỳ "kế tiếp".
 */
export const RecordStatus = ({ startAt, endAt }) => {
  const now = Date.now();
  if (new Date(endAt).getTime() <= now) return <Badge colorScheme="gray">Đã hết hạn</Badge>;
  if (new Date(startAt).getTime() > now) return <Badge colorScheme="orange">Kỳ kế tiếp</Badge>;
  return <Badge colorScheme="green">Đang dùng</Badge>;
};
const TIERS = [
  { key: 'free', name: 'Free', price: '0đ', feats: ['1 máy', 'Trắc nghiệm cơ bản'] },
  { key: 'standard', name: 'Standard', price: '79k/tháng', feats: ['2 máy', 'Đổi thưởng'] },
  { key: 'pro', name: 'Pro', price: '149k/tháng', feats: ['4 máy', 'Thách đấu · Báo cáo'] },
];

export default function Users() {
  // id nằm trên URL (/admin/users/:id) để F5 vẫn ở đúng hồ sơ, và chia sẻ được link.
  const { id } = useParams();
  const navigate = useNavigate();
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      {id
        ? <Detail id={id} onBack={() => navigate('/admin/users')} />
        : <List onOpen={(uid) => navigate(`/admin/users/${uid}`)} />}
    </Box>
  );
}

function List({ onOpen }) {
  const [q, setQ] = useState('');
  const dq = useDebounced(q);
  // Lọc + phân trang chạy trong DB: danh sách phụ huynh sẽ dài dần theo thời gian.
  const pager = useServerPaged((p) => api.get(`/admin/accounts?page=${p.page}&pageSize=${p.pageSize}${dq.trim() ? `&q=${encodeURIComponent(dq.trim())}` : ''}`),
    { initialSize: 20, resetKey: dq.trim() });
  const { loading, error } = pager;
  const border = useColorModeValue('#EAE7F5', '#2A2350');
  const avaBg = useColorModeValue('linear-gradient(150deg,#eae3ff,#d7c7ff)', 'linear-gradient(150deg,#2a2358,#3a2f74)');
  const rowBg = useColorModeValue('#fff', 'navy.700');
  if (error) return <Text color="red.500">{error}</Text>;
  return (
    <>
      <Flex align="center" gap="10px" mb="14px" wrap="wrap">
        <Text fontWeight="850" fontSize="18px">👥 Phụ huynh ({pager.total})</Text>
        {loading && <Spinner size="sm" />}
        <Input ml="auto" maxW="280px" size="sm" borderRadius="10px" placeholder="🔍 Tìm tên / email..."
          value={q} onChange={(e) => setQ(e.target.value)} />
      </Flex>
      {pager.pageItems.map((a) => {
        const [label, color] = TIER_PILL[a.tier] || TIER_PILL.free;
        return (
          <Flex key={a.id} align="center" gap="14px" p="14px" mb="10px" borderRadius="16px" border="1px solid" borderColor={border}
            bg={rowBg} cursor="pointer" onClick={() => onOpen(a.id)}
            _hover={{ boxShadow: '0 10px 30px rgba(85,60,160,.10)' }}>
            <Flex w="46px" h="46px" borderRadius="14px" bg={avaBg} align="center" justify="center" fontSize="22px" flex="0 0 auto">👤</Flex>
            <Box flex="1">
              <Text fontWeight="800">{a.displayName || a.email || '(không tên)'}</Text>
              <Text fontSize="12.5px" color="secondaryGray.600">{a.email} · 👧 {a._count.children} con · 📱 {a._count.devices} máy</Text>
            </Box>
            <Badge colorScheme={color} borderRadius="99px" px="10px" py="3px">{label}</Badge>
            <Button size="sm" variant="outline" ml="6px">Xem →</Button>
          </Flex>
        );
      })}
      {!pager.total && !loading && <Card><Text>{dq.trim() ? 'Không tìm thấy phụ huynh khớp.' : 'Chưa có phụ huynh nào.'}</Text></Card>}
      <Pagination pager={pager} label="tài khoản" />
    </>
  );
}

function Detail({ id, onBack }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi(() => api.get(`/admin/accounts/${id}`), [id]);
  const [busy, setBusy] = useState(false);
  const grant = useDisclosure();   // modal cấp gói thủ công
  const avaBg = useColorModeValue('linear-gradient(150deg,#eae3ff,#d7c7ff)', 'linear-gradient(150deg,#2a2358,#3a2f74)');
  const headBg = useColorModeValue('#FBFAFF', '#1E1840');
  const line = useColorModeValue('#D8D5EC', '#2A2350');

  if (loading) return <><Button variant="ghost" mb="12px" onClick={onBack}>‹ Danh sách</Button><Spinner /></>;
  if (error) return <Text color="red.500">{error}</Text>;

  const sub = data.subscription;
  const days = sub?.expiresAt ? Math.max(0, Math.ceil((new Date(sub.expiresAt) - Date.now()) / 86400000)) : null;
  const devByChild = (cid) => data.devices.filter((d) => d.childId === cid);

  async function changeTier(tier) {
    setBusy(true);
    try { await api.patch(`/admin/accounts/${id}/tier`, { tier }); toast({ title: `Đã đổi sang ${tier.toUpperCase()}`, status: 'success' }); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }
  async function del() {
    if (!window.confirm('Xoá tài khoản này cùng toàn bộ con/thiết bị/dữ liệu?')) return;
    try { await api.del(`/admin/accounts/${id}`); toast({ title: 'Đã xoá tài khoản', status: 'success' }); onBack(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  async function resetPw() {
    const p = window.prompt('Mật khẩu mới cho phụ huynh (tối thiểu 6 ký tự):', 'vuidehoc123');
    if (!p) return;
    if (p.length < 6) { toast({ title: 'Mật khẩu tối thiểu 6 ký tự', status: 'warning' }); return; }
    try { await api.post(`/admin/accounts/${id}/password`, { password: p }); toast({ title: `Đã đặt lại mật khẩu: ${p}`, status: 'success', duration: 4000 }); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }
  async function renameChild(child) {
    const name = window.prompt('Tên mới cho con:', child.name);
    if (name == null) return;
    const trimmed = name.trim();
    if (!trimmed) { toast({ title: 'Tên không được để trống', status: 'warning' }); return; }
    if (trimmed.length > 40) { toast({ title: 'Tên tối đa 40 ký tự', status: 'warning' }); return; }
    if (trimmed === child.name) return;
    try { await api.patch(`/children/${child.id}`, { name: trimmed }); toast({ title: `Đã đổi tên: ${trimmed}`, status: 'success' }); reload(); }
    catch (e) { toast({ title: e.message, status: 'error' }); }
  }

  return (
    <>
      <Flex align="center" mb="14px" gap="10px" wrap="wrap">
        <Button variant="ghost" onClick={onBack}>‹ Danh sách</Button>
        <Box flex="1" />
        <Button colorScheme="brand" variant="outline" size="sm" onClick={resetPw}>🔑 Đặt lại mật khẩu</Button>
        <Button colorScheme="red" variant="outline" size="sm" onClick={del}>Xoá tài khoản</Button>
      </Flex>

      <Card mb="16px">
        <Flex align="center" gap="14px">
          <Flex w="54px" h="54px" borderRadius="15px" bg={avaBg} align="center" justify="center" fontSize="26px">👤</Flex>
          <Box><Text fontWeight="850" fontSize="19px">{data.displayName || data.email}</Text>
            <Text fontSize="13px" color="secondaryGray.600">{data.email}</Text></Box>
        </Flex>
      </Card>

      {/* Gói & Subscription */}
      <Card mb="16px">
        <Flex align="center" mb="16px" wrap="wrap" gap="8px"><Text fontWeight="850" fontSize="15px">💳 Gói &amp; Subscription</Text>
          <Text ml="auto" fontSize="13px" color="secondaryGray.600">
            {sub?.status === 'active'
              ? <>Đang hoạt động{days != null ? <> · còn <b>{days} ngày</b> · gia hạn {new Date(sub.expiresAt).toLocaleDateString('vi-VN')}</> : ''}</>
              : 'Chưa có gói trả phí'}
          </Text>
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 3 }} gap="14px">
          {TIERS.map((t) => {
            const cur = data.tier === t.key;
            return (
              <Box key={t.key} position="relative" borderRadius="18px" p="18px"
                border="1.5px solid" borderColor={cur ? 'brand.500' : line}
                boxShadow={cur ? '0 0 0 3px rgba(124,92,252,.15)' : 'none'}>
                {cur && <Badge position="absolute" top="-11px" left="50%" transform="translateX(-50%)" colorScheme="purple" borderRadius="99px" px="12px">✓ Đang dùng</Badge>}
                <Text fontWeight="850" fontSize="16px">{t.name}</Text>
                <Text fontWeight="850" fontSize="20px" my="6px">{t.price}</Text>
                {t.feats.map((f) => <Text key={f} fontSize="13px" color="secondaryGray.600">✓ {f}</Text>)}
                {!cur && <Button mt="12px" size="sm" w="100%" colorScheme="brand" isLoading={busy} onClick={() => changeTier(t.key)}>
                  {TIERS.findIndex((x) => x.key === t.key) > TIERS.findIndex((x) => x.key === data.tier) ? 'Nâng cấp' : 'Chuyển'}</Button>}
              </Box>
            );
          })}
        </SimpleGrid>
        <Flex mt="14px" gap="10px" align="center" wrap="wrap">
          <Button size="sm" colorScheme="green" onClick={() => grant.onOpen()}>💵 Cấp gói đã thanh toán ngoài</Button>
          <Text fontSize="12.5px" color="secondaryGray.600">
            Dùng cho bản tải từ website (không qua Google Play): ghi cả hạn dùng và doanh thu.
          </Text>
        </Flex>
      </Card>

      <GrantPlanModal isOpen={grant.isOpen} onClose={grant.onClose} accountId={id} onDone={reload} />

      {/* Lịch sử gói / thanh toán */}
      <Card mb="16px" p="0" overflow="hidden">
        <Text fontWeight="850" fontSize="15px" p="16px 18px 10px">🧾 Lịch sử gói ({(data.subRecords || []).length})</Text>
        {(data.subRecords || []).length === 0
          ? <Text px="18px" pb="16px" fontSize="sm" color="secondaryGray.600">Chưa có lịch sử thanh toán.</Text>
          : (
            <Box overflowX="auto">
              <Box as="table" w="100%" fontSize="13.5px" style={{ borderCollapse: 'collapse' }}>
                <Box as="thead"><Box as="tr">
                  {['Gói', 'Trạng thái', 'Giao dịch lúc', 'Ngày bắt đầu', 'Ngày kết thúc', 'Thanh toán'].map((h) => (
                    <Box as="th" key={h} textAlign={h === 'Thanh toán' ? 'right' : 'left'} p="10px 18px" fontSize="11.5px"
                      textTransform="uppercase" letterSpacing=".05em" color="secondaryGray.600" fontWeight="800"
                      bg={headBg} whiteSpace="nowrap">{h}</Box>
                  ))}
                </Box></Box>
                <Box as="tbody">
                  {[...data.subRecords].sort((a, b) => new Date(b.endAt) - new Date(a.endAt)).map((r) => (
                    <Box as="tr" key={r.id}>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line">
                        <RecordTier tier={r.tier} source={r.source} />
                      </Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line"><RecordStatus startAt={r.startAt} endAt={r.endAt} /></Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line" whiteSpace="nowrap" color="secondaryGray.600">{fmtDateTime(r.createdAt)}</Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line">{new Date(r.startAt).toLocaleDateString('vi-VN')}</Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line" fontWeight="700">{new Date(r.endAt).toLocaleDateString('vi-VN')}</Box>
                      <Box as="td" p="11px 18px" borderTop="1px solid" borderColor="line" textAlign="right" fontWeight="800">{Number(r.amount).toLocaleString('vi-VN')} đ</Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
      </Card>

      {/* Con & thiết bị gộp */}
      <Text fontWeight="850" fontSize="15px" mb="12px">👧 Con &amp; thiết bị ({data.children.length})</Text>
      {data.children.map((c) => (
        <Card key={c.id} mb="12px">
          <Flex align="center" gap="12px">
            <Flex w="48px" h="48px" borderRadius="14px" bg={avaBg} align="center" justify="center" fontSize="24px">{kidAva(c)}</Flex>
            <Box flex="1"><Text fontWeight="800">{c.name}</Text>
              <Text fontSize="12.5px" color="secondaryGray.600">{String(c.grade).toUpperCase()} · {c.subject} · 💎 {c.stats?.balance ?? 0} · 🎯 {c.stats?.accuracy ?? 0}%</Text></Box>
            <Button size="xs" variant="outline" onClick={() => renameChild(c)}>✏️ Sửa tên</Button>
          </Flex>
          <Box mt="10px" pt="10px" borderTop="1px dashed" borderColor="line">
            {devByChild(c.id).length === 0
              ? <Text fontSize="13px" color="secondaryGray.600">Chưa có thiết bị gắn cho con này.</Text>
              : devByChild(c.id).map((d) => (
                <Flex key={d.id} align="center" gap="10px" py="6px" fontSize="13.5px" cursor="pointer"
                  borderRadius="10px" mx="-6px" px="6px" transition=".15s"
                  _hover={{ bg: 'blackAlpha.50', _dark: { bg: 'whiteAlpha.100' } }}
                  onClick={() => navigate(`/admin/devices/${d.id}`)}>
                  <span>📱</span> {d.model || d.platform || 'Thiết bị'}
                  <Badge colorScheme={d.online ? 'green' : 'gray'} ml="2px">{d.online ? 'online' : 'offline'}</Badge>
                  {!d.active && <Badge colorScheme="red">đã tắt</Badge>}
                  <Text ml="auto" fontSize="12px" color="secondaryGray.600">Xem →</Text>
                </Flex>
              ))}
          </Box>
        </Card>
      ))}
      {/* thiết bị chưa gán con */}
      {data.devices.filter((d) => !d.childId).length > 0 && (
        <Card mb="16px"><Text fontWeight="700" mb="8px" fontSize="14px">📱 Thiết bị chưa gán con</Text>
          {data.devices.filter((d) => !d.childId).map((d) => (
            <Flex key={d.id} align="center" gap="10px" py="6px" fontSize="13.5px" cursor="pointer"
              borderRadius="10px" mx="-6px" px="6px" transition=".15s"
              _hover={{ bg: 'blackAlpha.50', _dark: { bg: 'whiteAlpha.100' } }}
              onClick={() => navigate(`/admin/devices/${d.id}`)}><span>📱</span> {d.model || d.platform}
              <Badge colorScheme={d.online ? 'green' : 'gray'}>{d.online ? 'online' : 'offline'}</Badge>
              <Text ml="auto" fontSize="12px" color="secondaryGray.600">Xem →</Text></Flex>
          ))}
        </Card>
      )}

      {/* Admin quản lý kho quà của phụ huynh */}
      <ParentRewards accountId={id} />
    </>
  );
}

const REWARD_ICONS = ['🎁', '🎮', '🧋', '🍦', '🍿', '🍫', '🧸', '🎨', '📺', '📱', '⚽', '🍕'];
function ParentRewards({ accountId }) {
  const toast = useToast();
  const { data, loading, error, reload } = useApi(() => api.get(`/rewards/items?accountId=${accountId}`), [accountId]);
  const [icon, setIcon] = useState('🎁');
  const [name, setName] = useState('');
  const [cost, setCost] = useState(100);
  const [busy, setBusy] = useState(false);

  async function add(e) {
    e.preventDefault();
    if (!name.trim()) return toast({ title: 'Nhập tên phần thưởng', status: 'warning' });
    setBusy(true);
    try {
      await api.post('/rewards/items', { accountId, name: name.trim(), icon, cost: Number(cost) });
      toast({ title: 'Đã thêm phần thưởng', status: 'success' }); setName(''); setCost(100); reload();
    } catch (err) { toast({ title: err.message, status: 'error' }); } finally { setBusy(false); }
  }
  async function toggle(it) { try { await api.patch(`/rewards/items/${it.id}`, { active: !it.active }); reload(); } catch (e) { toast({ title: e.message, status: 'error' }); } }
  async function del(id) { if (!window.confirm('Xoá phần thưởng?')) return; try { await api.del(`/rewards/items/${id}`); reload(); } catch (e) { toast({ title: e.message, status: 'error' }); } }

  return (
    <Card>
      <Text fontWeight="850" fontSize="15px" mb="10px">🎁 Kho phần thưởng của phụ huynh</Text>
      {loading ? <Spinner /> : error ? <Text color="red.500">{error}</Text> : (data || []).map((it) => (
        <Flex key={it.id} align="center" py="9px" borderBottom="1px solid" borderColor="line">
          <Text fontSize="22px" me="10px">{it.icon}</Text>
          <Box flex="1"><Text fontWeight="700">{it.name}</Text><Text fontSize="12.5px" color="secondaryGray.600">💎 {it.cost}</Text></Box>
          <Switch isChecked={it.active} onChange={() => toggle(it)} colorScheme="green" me="8px" />
          <IconButton aria-label="Xoá" icon={<MdDelete />} size="sm" variant="ghost" colorScheme="red" onClick={() => del(it.id)} />
        </Flex>
      ))}
      <Box as="form" onSubmit={add} mt="14px">
        <Text fontWeight="700" fontSize="13px" mb="8px">Thêm phần thưởng giúp phụ huynh</Text>
        <Flex gap="8px" mb="8px" wrap="wrap">
          <Select w="90px" value={icon} onChange={(e) => setIcon(e.target.value)}>{REWARD_ICONS.map((i) => <option key={i} value={i}>{i}</option>)}</Select>
          <Input flex="1" minW="160px" placeholder="Tên phần thưởng" value={name} onChange={(e) => setName(e.target.value)} />
          <Input w="110px" type="number" placeholder="Giá 💎" value={cost} onChange={(e) => setCost(e.target.value)} />
        </Flex>
        <Button type="submit" colorScheme="brand" size="sm" isLoading={busy}>Thêm</Button>
      </Box>
    </Card>
  );
}

/**
 * Cấp gói cho tài khoản đã trả tiền NGOÀI app (bản tải từ website, không qua Google Play).
 * Khác nút "Nâng cấp/Chuyển" ở trên: nút kia chỉ đổi tier và không có hạn; cái này tạo kỳ có
 * ngày hết hạn + ghi vào lịch sử gói và doanh thu, giống hệt một lần mua thật.
 */
function GrantPlanModal({ isOpen, onClose, accountId, onDone }) {
  const toast = useToast();
  const [tier, setTier] = useState('pro');
  const [months, setMonths] = useState(1);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTier('pro'); setMonths(1); setNote(''); setAmount('');
  }, [isOpen]);

  // Gợi ý số tiền theo bảng giá để admin khỏi tính nhẩm; vẫn sửa được.
  const suggested = (tier === 'pro' ? 149000 : 79000) * months;

  async function submit() {
    setBusy(true);
    try {
      const r = await api.post(`/admin/accounts/${accountId}/grant-plan`, {
        tier, months: Number(months), amount: Number(amount === '' ? suggested : amount), note: note.trim() || undefined,
      });
      toast({
        title: `Đã cấp ${tier.toUpperCase()} ${months} tháng`,
        description: `Đến ${new Date(r.endAt).toLocaleDateString('vi-VN')}`,
        status: 'success',
      });
      onClose(); onDone && onDone();
    } catch (e) { toast({ title: e.message, status: 'error' }); } finally { setBusy(false); }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Cấp gói đã thanh toán ngoài</ModalHeader>
        <ModalBody>
          <Text fontSize="sm" color="secondaryGray.600" mb="12px">
            Kỳ mới nối tiếp kỳ còn hạn (nếu có), không chồng lấn ngày.
          </Text>
          <Text fontSize="13px" fontWeight="700" mb="4px">Gói</Text>
          <Select value={tier} onChange={(e) => setTier(e.target.value)} mb="12px">
            <option value="standard">Standard — 79.000đ/tháng</option>
            <option value="pro">Pro — 149.000đ/tháng</option>
          </Select>
          <Text fontSize="13px" fontWeight="700" mb="4px">Số tháng</Text>
          <Select value={months} onChange={(e) => setMonths(Number(e.target.value))} mb="12px">
            {[1, 3, 6, 12].map((m) => <option key={m} value={m}>{m} tháng</option>)}
          </Select>
          <Text fontSize="13px" fontWeight="700" mb="4px">Số tiền đã nhận (đ)</Text>
          <Input type="number" value={amount} placeholder={String(suggested)}
            onChange={(e) => setAmount(e.target.value)} mb="12px" />
          <Text fontSize="13px" fontWeight="700" mb="4px">Ghi chú (tuỳ chọn)</Text>
          <Input value={note} placeholder="VD: CK Vietcombank 09/08, mã GD 1234"
            onChange={(e) => setNote(e.target.value)} />
        </ModalBody>
        <ModalFooter gap="8px">
          <Button variant="ghost" onClick={onClose}>Huỷ</Button>
          <Button colorScheme="green" onClick={submit} isLoading={busy}>Cấp gói</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
